<template>
  <!-- Two live regions: assertive for errors/warnings, polite for success/info.
       Screen readers announce new toasts without stealing focus. -->
  <div class="toast-host" aria-live="off">
    <div class="sr-region" role="alert" aria-live="assertive" aria-atomic="false">
      <p v-for="t in assertive" :key="t.id" class="sr-only">{{ srText(t) }}</p>
    </div>
    <div class="sr-region" role="status" aria-live="polite" aria-atomic="false">
      <p v-for="t in polite" :key="t.id" class="sr-only">{{ srText(t) }}</p>
    </div>

    <TransitionGroup name="toast" tag="div" class="toast-stack">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="toast"
        :class="'toast-' + toast.type"
        @mouseenter="pause(toast.id)"
        @mouseleave="resume(toast.id)"
        @focusin="pause(toast.id)"
        @focusout="resume(toast.id)"
      >
        <span class="toast-icon" aria-hidden="true" v-html="ICONS[toast.type]"></span>
        <div class="toast-body">
          <p v-if="toast.title" class="toast-title">{{ toast.title }}</p>
          <p class="toast-msg">{{ toast.message }}</p>
        </div>
        <button class="toast-close" :aria-label="t('common.cancel')" @click="dismiss(toast.id)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useToast } from '@/composables/useToast'
import { useI18n } from '@/composables/useI18n'

const ICONS = {
  success: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M20 6 9 17l-5-5"/></svg>',
  error:   '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
  warning: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
  info:    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>',
}

export default {
  name: 'ToastHost',
  setup() {
    const { toasts, dismiss, pause, resume } = useToast()
    const { t } = useI18n()
    const assertive = computed(() => toasts.filter(x => x.type === 'error' || x.type === 'warning'))
    const polite    = computed(() => toasts.filter(x => x.type === 'success' || x.type === 'info'))
    const srText = (x) => (x.title ? x.title + '. ' : '') + x.message
    return { toasts, dismiss, pause, resume, assertive, polite, srText, ICONS, t }
  },
}
</script>

<style scoped>
/* Stack in a fixed corner, above everything, never blocking the center. */
.toast-host { position: fixed; inset: 0; pointer-events: none; z-index: 2000; }
.toast-stack {
  position: absolute;
  inset-block-end: 18px; inset-inline-end: 18px;
  display: flex; flex-direction: column; gap: 10px;
  width: min(380px, calc(100vw - 36px));
}

.toast {
  pointer-events: auto;
  display: flex; align-items: flex-start; gap: 11px;
  padding: 12px 12px 12px 14px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-inline-start: 4px solid var(--t-accent);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(16,22,40,.16);
}
/* Soft pastel tint per type (no saturated fills, per the design system). */
.toast-success { --t-accent: var(--success); }
.toast-error   { --t-accent: var(--danger); }
.toast-warning { --t-accent: var(--warning); }
.toast-info    { --t-accent: #5B6BD6; }
.toast { background: color-mix(in srgb, var(--t-accent) 7%, var(--surface)); }

.toast-icon { color: var(--t-accent); flex-shrink: 0; display: flex; margin-top: 1px; }
.toast-body { flex: 1; min-width: 0; }
.toast-title { font-size: 13.5px; font-weight: 700; margin-bottom: 2px; }
.toast-msg { font-size: 13px; line-height: 1.4; color: var(--text); word-break: break-word; }
.toast-close {
  flex-shrink: 0; border: none; background: none; cursor: pointer;
  color: var(--muted); padding: 3px; border-radius: 7px; display: flex;
}
.toast-close:hover { color: var(--text); background: var(--hover); }

/* Enter/leave + reflow animations. */
.toast-enter-from { opacity: 0; transform: translateY(10px) scale(.98); }
.toast-leave-to   { opacity: 0; transform: translateX(16px); }
.toast-enter-active, .toast-leave-active { transition: opacity .22s ease, transform .22s ease; }
.toast-move { transition: transform .22s ease; }

.sr-region { position: absolute; }
.sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}

@media (max-width: 520px) {
  .toast-stack { inset-inline: 12px; width: auto; inset-block-end: 12px; }
}
</style>
